import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { placementActivityApi } from '../../../services/api/placementActivityApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ItemTitle, OrderBadge, ColorDot, ActionButtons } from '../../IDPConfiguration/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const DEFAULT_ITEM = { name: '', code: '', description: '', color: '#2563EB', order: 0 };

const PlacementStatusTypes = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(DEFAULT_ITEM);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await placementActivityApi.getAllStatusTypes();
      setItems(data);
    } catch (err) {
      showAlert('Failed to load placement status types.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setIsEditing(true);
    } else {
      setCurrentItem({ ...DEFAULT_ITEM, order: items.length + 1 });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: name === 'order' ? (parseInt(value, 10) || 0) : value,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await placementActivityApi.updateStatusType(currentItem.id, currentItem);
        showAlert('Placement status type updated successfully', 'success');
      } else {
        await placementActivityApi.createStatusType(currentItem);
        showAlert('Placement status type created successfully', 'success');
      }
      handleCloseDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} placement status type.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await placementActivityApi.deleteStatusType(currentItem.id);
      showAlert('Placement status type deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || 'Failed to delete placement status type.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    const q = (searchTerm || '').toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      item.code.toLowerCase().includes(q)
    );
  });

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Placement Status Types</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search by name or code"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Status Type
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{ color: '#64748B', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontWeight: 600 }}>{filteredItems.length}</span>
            {filteredItems.length === 1 ? 'type' : 'types'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No placement status types found"
              description={
                searchTerm
                  ? 'No types match your search criteria.'
                  : "No placement status types have been added yet. Click '+ Add Status Type' to create one."
              }
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <ItemTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ColorDot $color={item.color} />
                        {item.name}
                      </Box>
                      <OrderBadge>Order: {item.order}</OrderBadge>
                    </ItemTitle>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#868e96',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        display: 'block',
                        marginBottom: '8px',
                      }}
                    >
                      Code: {item.code}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" sx={{ color: '#64748B', fontSize: '13px', marginBottom: '8px' }}>
                        {item.description}
                      </Typography>
                    )}
                    <ActionButtons>
                      <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDeleteDialog(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Status Type' : 'Add Status Type'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name *"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="code"
            label="Code * (unique slug, e.g. shortlisted)"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.code}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={currentItem.description}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="color"
            label="Color"
            type="color"
            fullWidth
            variant="outlined"
            value={currentItem.color}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ColorDot $color={currentItem.color} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentItem.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentItem.name.trim() || !currentItem.code.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the status type &ldquo;{currentItem.name}&rdquo;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlacementStatusTypes;
